import { useQuery } from "@tanstack/react-query";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";
import { FaqList, FaqSchema } from "@/components/SeoContent";
import { api } from "@/lib/api";

export default function PreviousResults() {
  const { data } = useQuery({
    queryKey: ["results-all"],
    queryFn: async () => (await api.get(`/results?page=1&limit=500`)).data,
    staleTime: 60_000,
  });

  const items = data?.items || [];

  return (
    <>
      <Seo
        title="Previous Juwai Morning Teer Results | Full Archive"
        description="Complete archive of previously published Juwai Morning Teer F/R and S/R results, ordered by date, newest first. For informational reference only."
        path="/previous-results"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Previous Results", path: "/previous-results" },
        ]}
      />
      <PageShell>
        <div
          className="rounded-2xl overflow-hidden border border-black/10 bg-white shadow-[0_10px_24px_-14px_rgba(0,0,0,0.20)]"
          data-testid="prev-results-table"
        >
          {/* dark header */}
          <div className="bg-neutral-900 text-white px-4 py-3.5 text-center">
            <h1
              className="font-display font-black tracking-wide text-sm sm:text-base"
              data-testid="prev-h1"
            >
              JUWAI MORNING TEER PREVIOUS RESULTS
            </h1>
          </div>
          {/* orange column-header row */}
          <div className="bg-[#F04E1E] text-white grid grid-cols-[2fr_1.4fr_0.7fr_0.7fr] px-3 py-2.5 text-[11px] sm:text-xs uppercase tracking-widest font-bold">
            <span className="text-center">City</span>
            <span className="text-center">Date</span>
            <span className="text-center">F/R</span>
            <span className="text-center">S/R</span>
          </div>
          {/* rows */}
          <div>
            {items.length === 0 && (
              <div className="p-8 text-center text-slate-500 text-sm" data-testid="prev-empty">
                No results have been published yet. Please check back later.
              </div>
            )}
            {items.map((it, i) => (
              <div
                key={it.id}
                className={`grid grid-cols-[2fr_1.4fr_0.7fr_0.7fr] px-3 py-3 items-center border-t border-black/5 ${
                  i % 2 ? "bg-[#F7F2EA]" : "bg-white"
                }`}
                data-testid={`prev-row-${it.date_key}`}
              >
                <span className="text-[13px] sm:text-sm font-semibold text-slate-800 text-center">
                  Juwai Morning Teer
                </span>
                <span className="num-font text-[13px] sm:text-sm font-bold text-slate-800 text-center">
                  {it.date}
                </span>
                <span className="num-font text-center text-[15px] sm:text-base font-black text-black">
                  {it.fr ?? "—"}
                </span>
                <span className="num-font text-center text-[15px] sm:text-base font-black text-black">
                  {it.sr ?? "—"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ===== Long-form SEO content ===== */}
        <section
          className="mt-6 rounded-2xl bg-white border border-black/5 p-5 sm:p-6"
          data-testid="prev-seo-content"
        >
          <h2 className="font-display font-black text-black text-2xl">
            Juwai Morning Teer Previous Results
          </h2>
          <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
            This page lists the full archive of previously published{" "}
            <strong>Juwai Morning Teer</strong> results, ordered by date, with
            the newest entry on top. Each row includes the draw date and the
            two round numbers — <strong>F/R</strong> (First Round, ~10:30 AM)
            and <strong>S/R</strong> (Second Round, ~11:30 AM). Because our
            team publishes every result manually, you get a clean, single
            reference archive rather than scattered numbers from mixed sources.
          </p>
          <h3 className="font-display font-black text-black text-lg mt-5">
            Why an archive of previous results matters
          </h3>
          <p className="mt-2 text-slate-700 text-[15px] leading-relaxed">
            A well-maintained previous-results table is useful for research
            and reference — for example, to review how a particular date was
            recorded, to compare weeks of a month, or simply to check what was
            published on any given day. It also acts as a transparency log:
            once a result is published, it stays here.
          </p>
          <h3 className="font-display font-black text-black text-lg mt-5">
            How to read the table
          </h3>
          <ul className="mt-2 space-y-2 text-slate-700 text-[15px] list-disc pl-5">
            <li>
              <strong>City</strong> — the fixed label “Juwai Morning Teer” for
              every row.
            </li>
            <li>
              <strong>Date</strong> — draw date in DD/MM/YYYY format.
            </li>
            <li>
              <strong>F/R</strong> — the two-digit First Round number.
            </li>
            <li>
              <strong>S/R</strong> — the two-digit Second Round number.
            </li>
          </ul>
          <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
            If today's result is not visible on the archive yet, please check
            the <a href="/" className="text-[#F04E1E] font-semibold">Home page</a>{" "}
            — the day's F/R and S/R appear there the moment we publish them
            and are added to this archive automatically.
          </p>
        </section>

        {/* ===== FAQ ===== */}
        <section className="mt-6" data-testid="prev-faq">
          <h2 className="font-display font-black text-black text-2xl px-1">
            Previous Results — FAQs
          </h2>
          <FaqList
            testIdPrefix="prev-faq"
            faqs={[
              {
                q: "How far back can I browse Juwai Morning Teer results?",
                a: "You can browse every result that our team has published on this site. Older entries appear further down the table.",
              },
              {
                q: "Do you delete or modify past results?",
                a: "No. Once a result is published it is preserved for transparency. If a genuine correction is needed, an editorial update is done and clearly noted.",
              },
              {
                q: "Are historical results available elsewhere on the site?",
                a: "Yes. The Historical Results page groups past results by month for easier browsing.",
              },
              {
                q: "Where can I see today's Juwai Morning Teer result?",
                a: "On the Home page — as soon as the day's F/R and S/R are declared, they appear on the Latest Result board.",
              },
            ]}
          />
          <FaqSchema
            id="previous-results"
            faqs={[
              { q: "How far back can I browse Juwai Morning Teer results?", a: "Every result we have published is visible on this page." },
              { q: "Do you delete or modify past results?", a: "No, results are preserved for transparency." },
              { q: "Are historical results available elsewhere?", a: "Yes, on the Historical Results page grouped by month." },
              { q: "Where can I see today's result?", a: "On the Home page." },
            ]}
          />
        </section>
      </PageShell>
    </>
  );
}
